import { useEffect, useState } from "react";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import { api, imgUrl, isVideo, GALLERY_CATEGORIES } from "../lib/api";
import { useSite } from "../context/SiteContext";
import { Reveal } from "../components/Reveal";
import { PageHero } from "./AboutPage";
import { Seo } from "../components/Seo";
import { PAGE_SEO } from "../lib/seo";

export default function ProjectsPage() {
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("All");
  const [index, setIndex] = useState(-1);
  const { L } = useSite();

  useEffect(() => { api.get("/gallery").then((r) => setItems(r.data)).catch(() => {}); }, []);

  const filtered = cat === "All" ? items : items.filter((g) => g.category === cat);
  const imagesOnly = filtered.filter((g) => !isVideo(g.image));
  const slides = imagesOnly.map((g) => ({ src: imgUrl(g.image), title: g.title, description: g.description }));

  return (
    <main data-testid="projects-page">
      <Seo title={PAGE_SEO.projects.title} description={PAGE_SEO.projects.description} path="/projects" />
      <PageHero overline={L.projects.overline} title={L.projects.title}
        subtitle={L.projects.subtitle} />
      <section className="py-20 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="flex flex-wrap gap-2 mb-12">
            {["All", ...GALLERY_CATEGORIES].map((c) => (
              <button key={c} onClick={() => setCat(c)} data-testid={`gallery-filter-${c.toLowerCase()}`}
                className={`px-5 py-2.5 font-heading font-semibold text-xs uppercase tracking-widest border transition-colors duration-200 ${
                  cat === c ? "bg-[#0B1B3D] text-white border-[#0B1B3D]" : "bg-white text-slate-600 border-slate-300 hover:border-[#0B1B3D]"}`}>
                {c === "All" ? L.projects.all : (L.projects.cats[c] || c)}
              </button>
            ))}
          </div>
          {filtered.length === 0 ? (
            <p className="text-slate-500 py-20 text-center">{L.projects.empty}</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((g, i) => (
                <Reveal key={g.id} delay={(i % 3) * 0.05}>
                  {isVideo(g.image) ? (
                    <div className="relative h-72 w-full bg-black" data-testid={`gallery-video-${i}`}>
                      <video src={imgUrl(g.image)} poster={g.thumbnail ? imgUrl(g.thumbnail) : undefined} controls muted playsInline className="w-full h-full object-cover" />
                      <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-[#0B1B3D]/80 to-transparent p-4 pointer-events-none">
                        <p className="text-blue-300 font-heading text-xs uppercase tracking-widest">{L.projects.cats[g.category] || g.category}</p>
                        <h3 className="font-heading font-bold text-white text-base">{g.title}</h3>
                      </div>
                    </div>
                  ) : (
                  <button onClick={() => setIndex(imagesOnly.indexOf(g))} data-testid={`gallery-item-${i}`} className="group relative overflow-hidden h-72 w-full text-left">
                    <img src={`${imgUrl(g.image)}?w=800&q=70`} alt={`${g.title} — Noyola Construction project in Huntsville, AL`} loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0B1B3D]/85 via-transparent to-transparent flex flex-col justify-end p-6">
                      <p className="text-blue-300 font-heading text-xs uppercase tracking-widest mb-1">{L.projects.cats[g.category] || g.category}</p>
                      <h3 className="font-heading font-bold text-white text-lg">{g.title}</h3>
                    </div>
                  </button>
                  )}
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
      <Lightbox open={index >= 0} index={index} close={() => setIndex(-1)} slides={slides} />
    </main>
  );
}
